/**
 * useDecisions Hook
 *
 * Drives the "Decisions captured" panel for one committed breakdown (FLOW-570):
 * loading whatever extraction already produced, confirming or dismissing a
 * proposed decision, and — the one bit of autonomy here — firing extraction
 * itself the first time the panel finds nothing captured for a committed
 * breakdown. That covers the user who commits and closes the tab before
 * extraction would otherwise have been triggered.
 *
 * Extraction is enrichment, not a gate: a failure is surfaced as a dismissible,
 * non-blocking error and never prevents the committed-items summary (or
 * anything else on the page) from rendering.
 */

import { Accessor, createEffect, createSignal, on } from "solid-js";
import type { Decision, DecisionId, IntakeId, ProjectId } from "../api/types";
import {
  confirmDecision,
  dismissDecision,
  extractDecisions,
  getIntakeDecisions,
} from "../api/decisions";
import { describeAiError, surfaceQuotaExhausted } from "../utils/aiError";

export interface UseDecisionsResult {
  /** Decisions captured for the current intake, in the order the API returned them */
  decisions: Accessor<Decision[]>;
  /** True once the initial lookup for the current intake has settled */
  checked: Accessor<boolean>;
  /** True while extraction is in flight */
  extracting: Accessor<boolean>;
  /** Human-readable failure from the last action, if it failed */
  error: Accessor<string | null>;
  /** Move a proposed decision to active. Resolves to whether it succeeded. */
  confirm: (projectId: ProjectId, id: DecisionId) => Promise<boolean>;
  /** Dismiss a proposed decision */
  dismiss: (projectId: ProjectId, id: DecisionId) => Promise<void>;
  /** Dismiss the current error */
  clearError: () => void;
}

export function useDecisions(
  intakeId: Accessor<IntakeId | undefined>,
  committed: Accessor<boolean>,
): UseDecisionsResult {
  const [decisions, setDecisions] = createSignal<Decision[]>([]);
  const [checked, setChecked] = createSignal(false);
  const [extracting, setExtracting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [attempted, setAttempted] = createSignal(false);

  const clear = () => {
    setDecisions([]);
    setChecked(false);
    setExtracting(false);
    setError(null);
    setAttempted(false);
  };

  const load = async (id: IntakeId) => {
    try {
      const response = await getIntakeDecisions(id);
      setDecisions(response.decisions);
    } catch (err) {
      setError(describeAiError(err));
    } finally {
      setChecked(true);
    }
  };

  const extract = async (id: IntakeId) => {
    setAttempted(true);
    setExtracting(true);
    setError(null);
    try {
      const response = await extractDecisions(id);
      setDecisions(response.decisions);
    } catch (err) {
      setError(describeAiError(err));
    } finally {
      setExtracting(false);
    }
  };

  // Load (or clear) whenever the intake we're reviewing changes.
  createEffect(
    on(intakeId, (id) => {
      clear();
      if (id) void load(id);
    }),
  );

  // Auto-fire extraction exactly once per intake: only once the initial load
  // has settled, only while nothing has been captured yet, and only for a
  // committed breakdown (extraction 409s otherwise).
  createEffect(() => {
    const id = intakeId();
    if (
      id &&
      committed() &&
      checked() &&
      !attempted() &&
      !extracting() &&
      decisions().length === 0
    ) {
      void extract(id);
    }
  });

  const confirm = async (
    projectId: ProjectId,
    id: DecisionId,
  ): Promise<boolean> => {
    setError(null);
    try {
      const updated = await confirmDecision(projectId, id);
      setDecisions((prev) => prev.map((d) => (d.id === id ? updated : d)));
      return true;
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
      return false;
    }
  };

  const dismiss = async (
    projectId: ProjectId,
    id: DecisionId,
  ): Promise<void> => {
    setError(null);
    try {
      await dismissDecision(projectId, id);
      setDecisions((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      surfaceQuotaExhausted(err);
      setError(describeAiError(err));
    }
  };

  return {
    decisions,
    checked,
    extracting,
    error,
    confirm,
    dismiss,
    clearError: () => setError(null),
  };
}
